/**
 * Project Settings tab: the study's details (editable), its identifiers, the email
 * registration window, a live enrollment funnel and the data-collection status.
 */
import { doc, serverTimestamp, Timestamp, updateDoc } from 'firebase/firestore';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useAlert } from '../../context/AlertContext';
import { db } from '../../firebase';
import EnrollmentFunnel from './EnrollmentFunnel';

const toDateInput = (ts) => {
  const d = ts?.toDate?.();
  if (!d) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const fromDateInput = (s, endOfDay) => {
  const [y, m, d] = s.split('-').map(Number);
  return Timestamp.fromDate(endOfDay ? new Date(y, m - 1, d, 23, 59, 59) : new Date(y, m - 1, d));
};

function CopyField({ label, value }) {
  const { addAlert } = useAlert();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      addAlert('success', 'Copied');
    } catch {
      addAlert('error', 'Could not copy; select the text and copy it instead.');
    }
  };
  return (
    <tr style={{ borderBottom: '1px solid var(--jp-gray-100)' }}>
      <td style={{ padding: '9px 0', color: 'var(--jp-gray-600)', width: 170, fontSize: 12, fontWeight: 500, verticalAlign: 'top' }}>{label}</td>
      <td style={{ padding: '9px 0', fontFamily: 'var(--jp-mono)', fontSize: 12, wordBreak: 'break-all' }}>{value}</td>
      <td style={{ padding: '6px 0 6px 12px', width: 70, textAlign: 'right' }}>
        <button type="button" className="ja-btn ja-btn-ghost" style={{ padding: '2px 8px', fontSize: 12 }} onClick={copy}>Copy</button>
      </td>
    </tr>
  );
}
CopyField.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.string.isRequired };

function Row({ label, children }) {
  return (
    <tr style={{ borderBottom: '1px solid var(--jp-gray-100)' }}>
      <td style={{ padding: '9px 0', color: 'var(--jp-gray-600)', width: 170, fontSize: 12, fontWeight: 500 }}>{label}</td>
      <td style={{ padding: '9px 0', fontSize: 13 }} colSpan={2}>{children}</td>
    </tr>
  );
}
Row.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node };

export default function ProjectSettingsPanel({ project, onOpenTab }) {
  const { addAlert } = useAlert();
  // Email registration (startEmailRegistration) reads settings.invitationWindow only.
  const win = project?.settings?.invitationWindow;
  const [name, setName] = useState(project.name || '');
  const [description, setDescription] = useState(project.description || '');
  const [from, setFrom] = useState(toDateInput(win?.from));
  const [to, setTo] = useState(toDateInput(win?.to));
  const [saving, setSaving] = useState(false);
  const [fcOn, setFcOn] = useState(project.futureContactOptIn === true);
  const [fcText, setFcText] = useState(project.futureContactText || '');
  useEffect(() => {
    setFcOn(project.futureContactOptIn === true);
    setFcText(project.futureContactText || '');
  }, [project.futureContactOptIn, project.futureContactText]);

  // Follow changes saved elsewhere (the project document is live).
  useEffect(() => { setName(project.name || ''); setDescription(project.description || ''); }, [project.name, project.description]);
  const fromKey = win?.from?.toMillis?.();
  const toKey = win?.to?.toMillis?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setFrom(toDateInput(win?.from)); setTo(toDateInput(win?.to)); }, [fromKey, toKey]);

  const detailsChanged = name.trim() !== (project.name || '') || description.trim() !== (project.description || '');
  const windowChanged = from !== toDateInput(win?.from) || to !== toDateInput(win?.to);
  const windowInvalid = (from && !to) || (!from && to) || (from && to && to < from);

  const save = async (fields, message) => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'projects', project.id), { ...fields, updatedAt: serverTimestamp() });
      addAlert('success', message);
    } catch (err) {
      addAlert('error', `Could not save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const now = Date.now();
  const fromMs = win?.from?.toMillis?.();
  const toMs = win?.to?.toMillis?.();
  const open = fromMs && toMs && now >= fromMs && now <= toMs;
  const daily = project.healthDailyFetch;
  const created = project.createdAt?.toDate?.()?.toLocaleString() || 'Unknown';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div className="ja-card">
        <div className="ja-card-header"><h3 className="ja-card-title">Study details</h3></div>
        <div className="ja-field">
          <label htmlFor="ps-name">Name</label>
          <input id="ps-name" className="ja-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
        </div>
        <div className="ja-field">
          <label htmlFor="ps-desc">Description</label>
          <textarea id="ps-desc" className="ja-textarea" rows={3} value={description} maxLength={2000}
            placeholder="What the study is about, who it recruits, IRB protocol number…"
            onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div className="ja-btn-row">
          <button type="button" className="ja-btn ja-btn-primary" disabled={saving || !detailsChanged || !name.trim()}
            onClick={() => save({ name: name.trim(), description: description.trim() }, 'Study details saved')}>
            Save details
          </button>
        </div>
      </div>

      <div className="ja-card">
        <div className="ja-card-header"><h3 className="ja-card-title">Identifiers</h3></div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            <CopyField label="Project ID" value={project.id} />
            <Row label="Created">{created}</Row>
          </tbody>
        </table>
        <p style={{ fontSize: 12, color: 'var(--jp-gray-600)', margin: '10px 0 0' }}>
          The Prolific study link and personal invite links are on the{' '}
          <button type="button" className="ja-btn ja-btn-ghost" style={{ padding: '0 4px', fontSize: 12 }} onClick={() => onOpenTab('Distribution')}>Distribution</button> tab.
        </p>
      </div>

      <div className="ja-card">
        <div className="ja-card-header">
          <h3 className="ja-card-title">Email registration window</h3>
          <span className={`ja-chip ${open ? 'ja-chip-active' : 'ja-chip-inactive'}`}>{open ? 'open now' : win ? 'closed now' : 'not set (closed)'}</span>
        </div>
        <p style={{ fontSize: 13, color: 'var(--jp-gray-600)', margin: '0 0 12px', lineHeight: 1.5 }}>
          New sign-ups by email are accepted only between these dates. Prolific and invite links are not limited by it;
          Prolific controls who can see the study.
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13 }}>From<br />
            <input type="date" className="ja-input" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label style={{ fontSize: 13 }}>To (inclusive)<br />
            <input type="date" className="ja-input" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
          <button type="button" className="ja-btn ja-btn-primary" disabled={saving || !windowChanged || windowInvalid}
            onClick={() => save({
              'settings.invitationWindow': from && to ? { from: fromDateInput(from), to: fromDateInput(to, true) } : null,
            }, 'Registration window saved')}>
            Save window
          </button>
        </div>
        {windowInvalid && <p style={{ fontSize: 12, color: '#B42318', margin: '8px 0 0' }}>Set both dates, with the end on or after the start.</p>}
      </div>

      <div className="ja-card">
        <div className="ja-card-header"><h3 className="ja-card-title">Enrollment</h3></div>
        <EnrollmentFunnel projectId={project.id} />
      </div>

      <div className="ja-card">
        <div className="ja-card-header">
          <h3 className="ja-card-title">Future studies</h3>
          <span className={`ja-chip ${project.futureContactOptIn ? 'ja-chip-active' : 'ja-chip-inactive'}`}>
            {project.futureContactOptIn ? 'offered' : 'off'}
          </span>
        </div>
        <p style={{ fontSize: 13, color: 'var(--jp-gray-600)', margin: '0 0 12px', lineHeight: 1.5 }}>
          Shows participants an optional &ldquo;contact me about future studies&rdquo; checkbox on the &ldquo;Your data&rdquo;
          summary after they connect. Turn it on only once this study&apos;s IRB approval covers recontact, and use the
          approved wording. It records permission to be contacted only; reusing data in another study needs that study&apos;s
          own consent.
        </p>
        <div className="ja-field">
          <label htmlFor="ps-fc-text">Wording shown to participants (blank = default)</label>
          <textarea id="ps-fc-text" className="ja-textarea" rows={3} value={fcText} maxLength={600}
            placeholder="You may contact me about future Jamasp studies. Joining a future study would need my separate consent, and I can change this choice at any time by contacting the research team."
            onChange={(e) => setFcText(e.target.value)} />
        </div>
        <div className="ja-btn-row">
          <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={fcOn} onChange={(e) => setFcOn(e.target.checked)} /> Offer to participants
          </label>
          <button type="button" className="ja-btn ja-btn-primary"
            disabled={saving || (fcOn === (project.futureContactOptIn === true) && fcText.trim() === (project.futureContactText || ''))}
            onClick={() => save({ futureContactOptIn: fcOn, futureContactText: fcText.trim() || null }, 'Future-studies option saved')}>
            Save
          </button>
        </div>
      </div>

      <div className="ja-card">
        <div className="ja-card-header"><h3 className="ja-card-title">Data collection</h3></div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            <Row label="Daily fetch">
              {daily?.enabled
                ? `On${daily.startDate || daily.endDate ? `, ${daily.startDate || '…'} to ${daily.endDate || '…'}` : ''}`
                : 'Off'}
              {' · '}
              <button type="button" className="ja-btn ja-btn-ghost" style={{ padding: '0 4px', fontSize: 12 }} onClick={() => onOpenTab('Data')}>change on the Data tab</button>
            </Row>
          </tbody>
        </table>
      </div>
    </div>
  );
}

ProjectSettingsPanel.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    description: PropTypes.string,
    createdAt: PropTypes.object,
    settings: PropTypes.object,
    healthDailyFetch: PropTypes.object,
    futureContactOptIn: PropTypes.bool,
    futureContactText: PropTypes.string,
  }).isRequired,
  onOpenTab: PropTypes.func.isRequired,
};
